import type { AttendanceStatus, Lesson, RoomBooking, TeacherDaySpan } from '@/api/types'
import { cn } from '@/lib/cn'
import { ETYKIETY_REZERWACJI } from '@/lib/roomBookings'
import { colorForId } from '@/lib/palette'
import {
  GRID_END_HOUR,
  GRID_START_HOUR,
  HOUR_HEIGHT,
  assignLanes,
  assignOverlapLanes,
  blockGeometry,
  formatTime,
  hoursInGrid,
} from '@/lib/scheduleTime'
import type { Lane } from '@/lib/scheduleTime'

/**
 * Kolor bloku zajęć, które już się rozliczyły, zależy od tego, co na nich zaszło.
 *
 * Jeden zielony dla wszystkiego z wpisem znaczyłby „obecność uzupełniona” —
 * a z grafiku czyta się co innego: czy lekcja przepadła i czy trzeba ją
 * odrobić. Zgłoszona nieobecność jest właśnie takim przypadkiem: zajęcia się
 * nie odbyły, uczniowi należy się nowy termin, a zielony blok mówił dokładnie
 * odwrotnie. Barwy te same co przy wpisywaniu obecności, żeby jedno oznaczało
 * to samo na obu ekranach.
 */
const TON_OBECNOSCI: Record<AttendanceStatus, string> = {
  PRESENT: 'border border-success/30 bg-success-container/60 text-on-success-container',
  ABSENT_NOTIFIED: 'border border-warning/40 bg-warning-container/70 text-on-warning-container',
  // Mocna obwódka zamiast pełnej czerwieni. Wypełniony czerwony blok mylił się
  // z burgundowym blokiem zajęć zaplanowanych — a to dwie przeciwne wiadomości.
  // Obwódka odróżnia go zarazem od zajęć odwołanych, które mają blade tło
  // i przekreślony podpis.
  ABSENT_UNNOTIFIED: 'border-2 border-error bg-error-container text-on-error-container',
}

export interface GridColumn {
  id: string
  label: string
  sublabel?: string
  color?: string
  /** Podświetlenie nagłówka — używane dla dzisiejszego dnia. */
  highlighted?: boolean
  lessons: Lesson[]
  /**
   * Rezerwacje sali poza zajęciami — remont, spotkanie, nagranie.
   *
   * Rysują się na tej samej osi co zajęcia, bo z punktu widzenia planującego
   * są tym samym: salą, której w tej godzinie nie ma. Bez nich grafik
   * pokazywałby wolne miejsce, w które i tak nie da się nic wpisać.
   */
  bookings?: RoomBooking[]
  /**
   * Zakresy pracy nauczycieli w tej kolumnie — grafik ogólny.
   *
   * Kolumna wypełnia albo `lessons`, albo `spans`; obie listy rysują się na tej
   * samej osi godzin, dzięki czemu oba grafiki są względem siebie czytelne.
   */
  spans?: TeacherDaySpan[]
}

/** Skąd blok bierze kolor. Domyślnie z motywu, `teacher` — z prowadzącego. */
export type ColorBy = 'default' | 'teacher'

interface TimeGridProps {
  columns: GridColumn[]
  onSelectLesson: (lesson: Lesson) => void
  /** Kliknięcie w rezerwację. Bez tej funkcji blok jest tylko informacją. */
  onSelectBooking?: (booking: RoomBooking) => void
  /** Kliknięcie w puste miejsce zakłada rezerwację o tej godzinie. */
  onSelectSlot?: (columnId: string, hour: number) => void
  emptyMessage: string
  colorBy?: ColorBy
  /**
   * Czy blok podpisuje się uczniem. Grafik sali odpowiada na pytanie „kto
   * zajmuje salę”, więc podpisem jest tam prowadzący — nazwiska uczniów nie mają
   * po co krążyć po ekranie oglądanym przy planowaniu.
   */
  showStudents?: boolean
}

/**
 * Siatka godzinowa z kolumnami. Kolumnami są sale (widok dnia) albo dni
 * tygodnia (widok tygodnia) — układ jest ten sam, zmienia się tylko podział.
 */
export function TimeGrid({
  columns,
  onSelectLesson,
  onSelectBooking,
  onSelectSlot,
  emptyMessage,
  colorBy = 'default',
  showStudents = true,
}: TimeGridProps) {
  const hours = hoursInGrid()
  const gridHeight = (GRID_END_HOUR - GRID_START_HOUR) * HOUR_HEIGHT

  if (columns.length === 0) {
    return <p className="px-6 py-16 text-center text-sm text-on-surface-variant">{emptyMessage}</p>
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[720px]">
        {/* Nagłówki kolumn. Bez `sticky` — kontener przewijany w poziomie
            przejmuje też oś pionową, przez co przyklejony nagłówek zostawiałby
            pusty pas nad siatką. */}
        <div
          className="grid border-b border-outline/10 bg-surface-container-low"
          style={{ gridTemplateColumns: `72px repeat(${columns.length}, minmax(140px, 1fr))` }}
        >
          <div className="px-3 py-4 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
            Czas
          </div>
          {columns.map((column) => (
            <div
              key={column.id}
              className={cn(
                'border-l border-outline/10 px-4 py-4',
                column.highlighted && 'bg-primary/5',
              )}
            >
              <div className="flex items-center gap-2">
                {column.color && (
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: column.color }}
                    aria-hidden="true"
                  />
                )}
                <p
                  className={cn(
                    'truncate font-headline text-sm font-bold',
                    column.highlighted ? 'text-primary' : 'text-secondary',
                  )}
                >
                  {column.label}
                </p>
              </div>
              {column.sublabel && (
                <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                  {column.sublabel}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Właściwa siatka */}
        <div
          className="relative grid"
          style={{
            gridTemplateColumns: `72px repeat(${columns.length}, minmax(140px, 1fr))`,
            height: gridHeight,
          }}
        >
          {/* Kolumna godzin. Etykieta zaczyna się na linii godziny, bez
              wyśrodkowania — inaczej pierwsza z nich wystawałaby poza siatkę. */}
          <div className="relative">
            {hours.slice(0, -1).map((hour) => (
              <div
                key={hour}
                className="absolute right-3 pt-1 text-[11px] font-bold text-on-surface-variant"
                style={{ top: (hour - GRID_START_HOUR) * HOUR_HEIGHT }}
              >
                {String(hour).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {columns.map((column) => (
            <div key={column.id} className="relative border-l border-outline/10">
              {/* Linie godzin i klikalne puste sloty */}
              {hours.slice(0, -1).map((hour) => (
                <button
                  key={hour}
                  type="button"
                  onClick={onSelectSlot ? () => onSelectSlot(column.id, hour) : undefined}
                  disabled={!onSelectSlot}
                  aria-label={`Dodaj zajęcia o ${String(hour).padStart(2, '0')}:00 — ${column.label}`}
                  className={cn(
                    'absolute left-0 w-full border-t border-outline/10 transition-colors',
                    onSelectSlot && 'hover:bg-primary/5',
                    !onSelectSlot && 'cursor-default',
                  )}
                  style={{ top: (hour - GRID_START_HOUR) * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                />
              ))}

              {/* Zajęcia i rezerwacje w tej samej godzinie rozsuwamy na tory.
                  W widoku dnia zdarza się to po zwolnieniu terminu, w widoku
                  tygodnia przy wszystkich salach — to normalny stan, nie wyjątek.

                  Jedna lista, bo z punktu widzenia patrzącego rezerwacja jest
                  takim samym zajętym pasem co lekcja; osobne warstwy nakładałyby
                  się na siebie.

                  Przy równych godzinach pierwsze idą bloki, które terminu nie
                  zajmują (odwołane, nieobecności zgłoszone w porę). Trafiają
                  wtedy na lewy tor i pod spód, a to, co salę faktycznie zajmuje,
                  zostaje czytelne. */}
              {assignOverlapLanes(blokiKolumny(column)).map(({ item, lane, lanes }) =>
                item.rodzaj === 'lekcja' ? (
                  <LessonBlock
                    key={item.id}
                    lesson={item.lesson}
                    lane={lane}
                    lanes={lanes}
                    onSelect={onSelectLesson}
                    colorBy={colorBy}
                    showStudents={showStudents}
                  />
                ) : (
                  <BookingBlock
                    key={item.id}
                    booking={item.booking}
                    lane={lane}
                    lanes={lanes}
                    onSelect={onSelectBooking}
                  />
                ),
              )}

              {assignLanes(column.spans ?? []).map((tor) => (
                <SpanBlock key={tor.item.teacher.id} {...tor} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Jedna rzecz zajmująca pas w kolumnie: zajęcia albo rezerwacja sali. */
type BlokKolumny =
  | { rodzaj: 'lekcja'; id: string; starts_at: string; ends_at: string; lesson: Lesson }
  | { rodzaj: 'rezerwacja'; id: string; starts_at: string; ends_at: string; booking: RoomBooking }

/**
 * Zajęcia i rezerwacje jednej kolumny w kolejności rysowania.
 *
 * Sortowanie po godzinie, a przy równym starcie — najpierw to, co terminu nie
 * zajmuje. Rezerwacja zajmuje go zawsze, więc idzie za odwołanymi zajęciami.
 */
function blokiKolumny(column: GridColumn): BlokKolumny[] {
  const bloki: BlokKolumny[] = [
    ...column.lessons.map((lesson): BlokKolumny => ({
      rodzaj: 'lekcja',
      id: lesson.id,
      starts_at: lesson.starts_at,
      ends_at: lesson.ends_at,
      lesson,
    })),
    ...(column.bookings ?? []).map((booking): BlokKolumny => ({
      rodzaj: 'rezerwacja',
      id: booking.id,
      starts_at: booking.starts_at,
      ends_at: booking.ends_at,
      booking,
    })),
  ]

  const zajmuje = (blok: BlokKolumny) =>
    blok.rodzaj === 'rezerwacja' ? 1 : Number(blok.lesson.blocks_slot)

  return bloki.sort((a, b) => a.starts_at.localeCompare(b.starts_at) || zajmuje(a) - zajmuje(b))
}

/**
 * Legenda kolorów prowadzących.
 *
 * Bez niej kolorowy blok niesie informację tylko dla kogoś, kto zna kod barwny
 * na pamięć. Kolejność alfabetyczna, bo listy nazwisk czyta się szukaniem.
 */
export function LegendaProwadzacych({ teachers }: { teachers: { id: string; name: string }[] }) {
  if (teachers.length === 0) return null

  const posortowani = [...teachers].sort((a, b) => a.name.localeCompare(b.name, 'pl'))

  return (
    <ul className="flex flex-wrap items-center gap-3" aria-label="Kolory prowadzących">
      {posortowani.map((teacher) => (
        <li key={teacher.id} className="flex items-center gap-1.5 text-xs text-on-surface-variant">
          <span
            className="h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: colorForId(teacher.id) }}
            aria-hidden="true"
          />
          {teacher.name}
        </li>
      ))}
    </ul>
  )
}

function SpanBlock({ item: span, lane, lanes }: Lane<TeacherDaySpan>) {
  const { top, height } = blockGeometry(span.starts_at, span.ends_at)
  const kolor = colorForId(span.teacher.id)
  const szerokosc = 100 / lanes

  return (
    <div
      className="absolute flex flex-col items-center justify-center overflow-hidden rounded-lg px-1.5 py-1 text-center text-white shadow-card"
      style={{
        top: `${top}%`,
        height: `${height}%`,
        left: `calc(${lane * szerokosc}% + 2px)`,
        width: `calc(${szerokosc}% - 4px)`,
        backgroundColor: kolor,
      }}
    >
      <p className="truncate text-xs font-bold leading-tight">{span.teacher.full_name}</p>
      <p className="text-[11px] font-semibold tabular-nums text-white/85">
        {formatTime(span.starts_at)}–{formatTime(span.ends_at)}
      </p>
    </div>
  )
}

/**
 * Do ilu równoczesnych zajęć dzielimy szerokość po równo.
 *
 * Kolumna tygodnia ma około 145 pikseli. Na pół to jeszcze 70 — mieści się
 * godzina i początek nazwiska. Na trzy zostaje 45, czyli sam skrót i wielokropek.
 *
 * Powyżej progu bloki układają się kaskadą: każdy kolejny odsunięty o stałe
 * `KASKADA_PRZESUNIECIE` i sięgający prawej krawędzi, rysowany nad poprzednim.
 * Ostatni zostaje czytelny w całości, a wystające z lewej paski mówią, ile
 * zajęć stoi w tej godzinie. Dzielenie po równo dawałoby tu trzy nieczytelne
 * słupki zamiast jednego czytelnego bloku i dwóch znaczników.
 *
 * Szczegóły takiej godziny czyta się w widoku dnia, gdzie każda sala ma własną
 * kolumnę — tam kilka zajęć naraz to rzadkość, nie reguła.
 */
const MAKS_ROWNYCH_TOROW = 2
const KASKADA_PRZESUNIECIE = 16

function LessonBlock({
  lesson,
  lane,
  lanes,
  onSelect,
  colorBy,
  showStudents = true,
}: {
  lesson: Lesson
  lane: number
  lanes: number
  onSelect: (lesson: Lesson) => void
  colorBy?: ColorBy
  showStudents?: boolean
}) {
  const { top, height } = blockGeometry(lesson.starts_at, lesson.ends_at)
  const szerokosc = 100 / lanes
  const polozenie =
    lanes > MAKS_ROWNYCH_TOROW
      ? { left: `${4 + lane * KASKADA_PRZESUNIECIE}px`, right: '4px', zIndex: lane + 1 }
      : { left: `calc(${lane * szerokosc}% + 4px)`, width: `calc(${szerokosc}% - 8px)` }
  const isCancelled = lesson.status === 'CANCELLED'
  const isCompleted = lesson.status === 'COMPLETED'
  // Cudze zajęcia w widoku nauczyciela: stonowany blok bez pełnego koloru,
  // żeby własny grafik dało się odczytać jednym spojrzeniem.
  const isForeign = !lesson.is_own

  // Na grafiku sali każdy prowadzący dostaje własny kolor — inaczej wszystkie
  // bloki wyglądają tak samo i nie widać, kto po kim wchodzi.
  //
  // Kolor wygrywa także z oznaczeniem „rozliczone”: w widoku sali liczy się,
  // kto ją zajmuje, a nie czy nauczyciel wpisał już obecność (od tego jest
  // ekran obecności). Wyjątkiem są zajęcia odwołane — te zwalniają salę, więc
  // muszą wyglądać inaczej niż zajmujące ją rezerwacje.
  const kolorNauczyciela =
    colorBy === 'teacher' && !isCancelled ? colorForId(lesson.teacher.id) : null

  // Cudze zajęcia w widoku nauczyciela nie niosą wpisu obecności (dane ucznia
  // są zastrzeżone), więc zostaje przy nich dotychczasowy zielony blok.
  const tonObecnosci =
    isCompleted && !isCancelled && kolorNauczyciela === null
      ? TON_OBECNOSCI[lesson.attendance?.status ?? 'PRESENT']
      : null

  const muted = kolorNauczyciela === null && (isCancelled || isCompleted || isForeign)

  return (
    <button
      type="button"
      onClick={() => onSelect(lesson)}
      // W kaskadzie z bloku zostaje wąski pasek — podpowiedź mówi, czyj jest,
      // bez otwierania szczegółów.
      title={`${formatTime(lesson.starts_at)}–${formatTime(lesson.ends_at)} · ${lesson.display_title} · ${lesson.teacher.full_name}`}
      className={cn(
        'absolute overflow-hidden rounded-lg px-2.5 py-1.5 text-left transition-all',
        'hover:z-20 hover:shadow-ambient focus-visible:z-20',
        isCancelled && 'border border-error/30 bg-error-container/50 text-on-error-container',
        tonObecnosci,
        isForeign &&
          kolorNauczyciela === null &&
          !isCancelled &&
          !isCompleted &&
          'border border-outline/25 bg-surface-container-high text-on-surface-variant',
        kolorNauczyciela !== null && 'text-white shadow-card',
        !muted && kolorNauczyciela === null && 'bg-primary text-on-primary shadow-card',
      )}
      style={{
        top: `${top}%`,
        height: `${height}%`,
        ...polozenie,
        ...(kolorNauczyciela !== null ? { backgroundColor: kolorNauczyciela } : {}),
      }}
    >
      <p
        className={cn('text-[10px] font-bold tabular-nums', muted ? 'opacity-70' : 'text-white/80')}
      >
        {formatTime(lesson.starts_at)}–{formatTime(lesson.ends_at)}
      </p>
      <p className={cn('truncate text-xs font-bold', isCancelled && 'line-through')}>
        {showStudents ? lesson.display_title : lesson.teacher.full_name}
      </p>
      {showStudents && (
        <p className={cn('truncate text-[10px]', muted ? 'opacity-70' : 'text-white/70')}>
          {lesson.teacher.full_name}
        </p>
      )}
    </button>
  )
}

/**
 * Sala zajęta na coś, co nie jest lekcją.
 *
 * Wygląd celowo inny niż każdych zajęć — kreskowana obwódka i szarość zamiast
 * koloru prowadzącego. Blok mówi „tu nikt się nie uczy, a sali nie ma”, więc
 * nie powinien się z lekcją mylić nawet kątem oka.
 */
function BookingBlock({
  booking,
  lane,
  lanes,
  onSelect,
}: {
  booking: RoomBooking
  lane: number
  lanes: number
  onSelect?: (booking: RoomBooking) => void
}) {
  const { top, height } = blockGeometry(booking.starts_at, booking.ends_at)
  const szerokosc = 100 / lanes
  const polozenie =
    lanes > MAKS_ROWNYCH_TOROW
      ? { left: `${4 + lane * KASKADA_PRZESUNIECIE}px`, right: '4px', zIndex: lane + 1 }
      : { left: `calc(${lane * szerokosc}% + 4px)`, width: `calc(${szerokosc}% - 8px)` }

  const etykieta = ETYKIETY_REZERWACJI[booking.kind]
  const podpis = [
    `${formatTime(booking.starts_at)}–${formatTime(booking.ends_at)}`,
    `${booking.room.name} · ${etykieta}`,
    booking.note,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <button
      type="button"
      onClick={onSelect ? () => onSelect(booking) : undefined}
      disabled={!onSelect}
      title={podpis}
      className={cn(
        'absolute overflow-hidden rounded-lg border-2 border-dashed border-outline/50 px-2.5 py-1.5',
        'bg-surface-container-highest text-left text-on-surface-variant transition-all',
        'hover:z-20 hover:shadow-ambient focus-visible:z-20',
        !onSelect && 'cursor-default',
      )}
      style={{ top: `${top}%`, height: `${height}%`, ...polozenie }}
    >
      <p className="text-[10px] font-bold tabular-nums opacity-70">
        {formatTime(booking.starts_at)}–{formatTime(booking.ends_at)}
      </p>
      <p className="truncate text-xs font-bold">{etykieta}</p>
      {booking.note && <p className="truncate text-[10px] opacity-70">{booking.note}</p>}
    </button>
  )
}
